import { Injectable } from '@angular/core';
import { PlayerService } from './player.service';
import { ControlService } from './control.service';
import { AppConstants } from 'src/app/app-constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { RealtimeService } from './realtime/realtime.service';
import { CallStoreService } from './call-store/call-store.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoService } from '@jsverse/transloco';
import { MediaStreamService } from './media-stream/media-stream.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { canShareScreen } from 'src/app/utils/device/device.util';
import { NativeCallService } from 'src/app/services/native-call.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceSettingsService {
  playerService: PlayerService | undefined;
  controlService: ControlService | undefined;
  realtimeService: RealtimeService | undefined;

  audioDevice: string = 'audio';
  audioDeviceSaved: string = '';
  audioDeviceSelected: string = '';
  isDiagnosticRunning: boolean = false;
  audioDevicePlaceholder: string = '';
  audioDeviceError: boolean = true;
  audioDeviceOptions: MediaDeviceInfo[] = [];
  audioStream: MediaStream | undefined;

  videoDevice: string = 'video';
  videoDeviceSaved: string = '';
  videoDeviceSelected: string = '';
  videoDevicePlaceholder: string = '';
  videoDeviceError: boolean = true;
  videoDeviceOptions: MediaDeviceInfo[] = [];
  videoStream: MediaStream | undefined;

  // audio levels
  public isUserSpeaking: boolean = false;
  public decibels: number = -50;
  public isAudioLevelDetectionActive: boolean = false;
  userAudioStream: MediaStream | undefined;
  audioContext: AudioContext;
  analyser: AnalyserNode;
  microphone: MediaStreamAudioSourceNode | undefined;
  animationFrame: number | undefined;
  allAudioStreams: MediaStream[] = [];

  private readonly displayModalSubject = new BehaviorSubject<boolean>(false);
  public readonly displayModal$ = this.displayModalSubject.asObservable();

  private _displayModal: boolean = false;
  public get displayModal(): boolean {
    return this._displayModal;
  }
  public set displayModal(value: boolean) {
    this._displayModal = value;
    this.displayModalSubject.next(value);
  }
  public isCameraUnavailable: boolean = true;
  isDeviceBlocked: boolean = false;
  isJoinInProgress: boolean = false;
  isPermissionDismissedAudio: boolean = false;
  isPermissionDismissedVideo: boolean = false;
  displayAudioOptions: boolean = true;
  isFirstLoad: boolean = false;
  isVideoDeviceAlertClosed: boolean = false;

  fromPauseModal: boolean = false;

  lastAudioDevice: string = 'lastAudioDevice';
  lastVideoDevice: string = 'lastVideoDevice';
  retryVideo: number = 0;
  retryAudio: number = 0;

  private deviceChangeHandler: () => void;
  // Guards against in-flight getUserMedia callbacks resolving after the call was exited.
  private deviceSessionId: number = 0;
  // Separate counter for startAudioLevelDetection — incremented by stopAudioLevelDetection
  // so it doesn't invalidate unrelated getUserMedia sessions (getDeviceAccess, setAudioDevice).
  private audioLevelSessionId: number = 0;

  private deviceSettingsConfirmed$ = new Subject<void>();
  public readonly onDeviceSettingsConfirmed$ = this.deviceSettingsConfirmed$.asObservable();

  constructor(
    private callStore: CallStoreService,
    private amplitudeService: AmplitudeService,
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private mediaStreamService: MediaStreamService,
    private logger: LoggerService,
    private nativeCallService: NativeCallService
  ) {
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.deviceChangeHandler = this.handleDeviceChange.bind(this);
    this.translationService.loadScopedTranslation('settings');
    this.translocoService
      .selectTranslate('BROWSER_UNABLE_TO_ACCESS', {}, 'settings')
      .subscribe((translated: string) => {
        this.audioDevicePlaceholder = translated;
        this.videoDevicePlaceholder = translated;
      });
  }

  setPlayerService(playerService: PlayerService): void {
    this.playerService = playerService;
  }

  setControlService(controlService: ControlService) {
    this.controlService = controlService;
  }

  setRealtimeService(realtimeService: RealtimeService) {
    this.realtimeService = realtimeService;
  }

  validateDeviceAccess() {
    this.isFirstLoad = false;
    this.isPermissionDismissedAudio = false;
    this.isPermissionDismissedVideo = false;
    const audioConstraint = this.getAudioConstraints();
    // validate if user has access to audio device
    if (this.audioDeviceSaved !== '') {
      // validate if user has access to audio device
      this.getDeviceAccess(this.audioDevice, audioConstraint, true);
      // if we are on the modal screen we get the video device access
      if (this.displayModal && !this.controlService?.isVisualAidCall) {
        this.getDeviceAccess(this.videoDevice, this.getVideoConstraints());
      }
    } else {
      // ask for audio and video device access
      this.startDeviceCheck();
    }
    // keep checking for device changes
    this.detectDeviceChanges();
  }

  getAudioConstraints(): MediaStreamConstraints {
    this.audioDeviceSaved =
      this.getAudioDeviceLocalStorage() ||
      localStorage.getItem(this.lastAudioDevice) ||
      '';
    return this.audioDeviceSaved
      ? { audio: { deviceId: { exact: this.audioDeviceSaved } }, video: false }
      : { audio: true, video: false };
  }

  getVideoConstraints(): MediaStreamConstraints {
    this.videoDeviceSaved = this.getVideoDeviceLocalStorage();
    return this.videoDeviceSaved
      ? { video: { deviceId: { exact: this.videoDeviceSaved } }, audio: false }
      : { video: true, audio: false };
  }

  getDeviceAccess(
    device: string,
    constraints: MediaStreamConstraints,
    shouldStartCall: boolean = false
  ) {
    const isVideoDevice = device === this.videoDevice;

    if (isVideoDevice) {
      const sessionId = this.deviceSessionId;
      this.mediaStreamService.createVideoStream(
        constraints
      ).then((stream) => {
        if (this.deviceSessionId !== sessionId) {
          this.mediaStreamService.stopVideoStream();
          return;
        }
        console.log(`${device} access is allowed (shared stream)`);
        console.log('contraints in get device access', constraints);

        this.isVideoDeviceAlertClosed = true;

        // For validation, we don't stop the stream - other services need it
        this.handleDeviceAccessSuccess(device, shouldStartCall);
      }).catch((error) => {
        if (this.deviceSessionId !== sessionId) return;
        // Only cleanup on error, not on success
        this.mediaStreamService.cleanupVideoStream();
        this.displayErrorMessage(device, error);
      }).finally(() => {
        if (this.deviceSessionId !== sessionId) return;
        if (device === this.videoDevice) {
          this.isVideoDeviceAlertClosed = true;
        }
      });
    } else {
      // Audio devices use original getUserMedia
      const sessionId = this.deviceSessionId;
      navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
        if (this.deviceSessionId !== sessionId) return; // stopDeviceSettings called while pending

        console.log(`${device} access is allowed`);
        console.log('contraints in get device access', constraints);

        this.handleDeviceAccessSuccess(device, shouldStartCall);
      }).catch((error) => {
        if (this.deviceSessionId !== sessionId) return;
        this.displayErrorMessage(device, error);
      });
    }
  }

  private handleDeviceAccessSuccess(device: string, shouldStartCall: boolean): void {
    const isJoinScreen = !this.displayModal;
    const isDeviceSaved = this.audioDeviceSaved.length > 0;

    if (isJoinScreen && shouldStartCall && isDeviceSaved) {
      // start call
      this.joinCall(false);
    } else {
      this.listDevices(device);
    }
    // remove error message
    this.displayDeviceError(device, false);
  }

  displayDeviceError(device: string, display: boolean) {
    if (device === this.audioDevice) {
      this.audioDeviceError = display;
    }
    if (device === this.videoDevice) {
      this.videoDeviceError = display;
    }
  }

  startDeviceCheck(): void {
    this.isFirstLoad = true;
    this.displayDeviceSettings();
    const audioConstraints = this.getAudioConstraintsOtherCall();

    this.getDeviceAccess(this.audioDevice, audioConstraints);

    const shouldRequestCamera = !this.controlService?.isVisualAidCall;
    console.log("Should request camera: ", shouldRequestCamera)
    if (shouldRequestCamera) {
      const videoConstraints = this.getVideoConstraintsOtherCall();
      this.getDeviceAccess(this.videoDevice, videoConstraints);
    }
  }

  displayDeviceSettings(): void {
    const viewDeviceSettings = AppConstants.VIEW_DEVICE_SETTINGS;
    const viewPractice = AppConstants.VIEW_PRACTICE;
    const isVisualAidCall = this.controlService?.isVisualAidCall;
    const practiceType = this.callStore.getModuleAssignmentType();
    const isPathway = practiceType === PathwayConstants.PRACTICE_TYPE_PATHWAY;

    if (isPathway) {
      setTimeout(() => {
        this.callStore.setCurrentView(viewDeviceSettings);
      }, 0);
      return;
    }

    if (this.playerService) {
      // change view if user is not in practice view or if it has not pre call clips
      if (
        this.callStore.getCurrentView() !== viewPractice ||
        (!this.playerService.hasPreCallClips && !isVisualAidCall)
      ) {
        // timeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          if (this.playerService) {
            this.callStore.setCurrentView(viewDeviceSettings);
          }
        }, 0);
      }
    }
  }

  listDevices(device: string) {
    const deviceKind = `${device}input`;
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const list = devices.filter(
          (device: MediaDeviceInfo) => device.kind === deviceKind
        );
        const selected = list.length > 0 ? list[0].deviceId : '';

        // Audio
        if (device === this.audioDevice) {
          this.audioDeviceOptions = list;
          this.audioDeviceSelected = this.audioDeviceSaved || selected;
          this.setAudioDevice();
        }

        // Video
        if (device === this.videoDevice) {
          this.videoDeviceOptions = list;
          this.videoDeviceSelected = this.videoDeviceSaved || selected;
          this.setVideoDevice();
        }

        // Log device settings when both devices are ready
        const hasAudioDevices = this.audioDeviceOptions.length > 0;
        const hasVideoDevices = this.videoDeviceOptions.length > 0;
        const isVideoRequired = !this.controlService?.isVisualAidCall;

        // Only log once when we have all the required device information
        if (hasAudioDevices && (hasVideoDevices || !isVideoRequired)) {
          // Check if we're in modal mode or initial screen mode
          const logMessage = this.displayModal
            ? '[CALL] device settings - modal'
            : '[CALL] device settings - screen';

          // Get current devices
          const currentAudioDevice = this.audioDeviceSelected;
          const currentVideoDevice = this.videoDeviceSelected;

          this.logger.log(logMessage, {
            isMicAccessAllowed: !this.audioDeviceError,
            audioDevicesAvailable: this.audioDeviceOptions.map(d => ({ deviceId: d.deviceId, label: d.label })),
            currentMic: currentAudioDevice,
            isVideoRequired: isVideoRequired,
            isVideoAccessAllowed: !this.videoDeviceError,
            videoDevicesAvailable: this.videoDeviceOptions.map(d => ({ deviceId: d.deviceId, label: d.label })),
            currentVideo: currentVideoDevice,
          });
        }
      })
      .catch((err) => {
        console.log(err.name + ': ' + err.message);
      });
  }

  setVideoDevice() {
    if (this.videoDeviceSelected !== '') {
      const sessionId = this.deviceSessionId;
      const constraints = {
        video: {
          deviceId: { exact: this.videoDeviceSelected },
        },
      };

      this.mediaStreamService.createVideoStream(constraints)
        .then((stream) => {
          if (this.deviceSessionId !== sessionId) {
            this.mediaStreamService.stopVideoStream();
            return;
          }
          this.videoStream = stream;

          // Wait for video element to be available in the DOM
          this.waitForVideoElement().then((video) => {
            try {
              video.srcObject = stream;
              video.play();
              this.isCameraUnavailable = false;
            } catch (error) {
              this.mediaStreamService.stopVideoStream();
              this.videoStream = undefined;
              this.isCameraUnavailable = true;

              // Log video playback error
              this.logger.error('[CALL] device settings - error playing video', error);

              throw new Error(
                'Error playing the user video on device settings service'
              );
            }
          });
        })
        .catch((error) => {
          if (this.deviceSessionId !== sessionId) return;
          this.displayErrorMessage(this.videoDevice, error);
          if (error.name === 'OverconstrainedError') {
            this.videoDeviceSelected = '';
            this.removeVideoDeviceLocalStorage();
            // timeout prevents the error of the camera not being found after the first attempt
            setTimeout(() => {
              if (this.deviceSessionId !== sessionId) return;
              this.setVideoDevice();
              if (this.retryVideo === 0) {
                this.retryVideo++;
                this.listDevices(this.videoDevice);
              }
            }, 3000);
          }
        });
    }
  }

  private waitForVideoElement(): Promise<HTMLVideoElement> {
    return new Promise((resolve, reject) => {
      const maxAttempts = 50; // 50 attempts * 100ms = 5 seconds max
      let attempts = 0;

      const checkElement = () => {
        const video =
          (document.getElementById('userCam') as HTMLVideoElement | null) ||
          (document.getElementById('user-video') as HTMLVideoElement | null);

        if (video) {
          resolve(video);
        } else if (attempts >= maxAttempts) {
          reject(new Error('Video element not found after waiting'));
        } else {
          attempts++;
          setTimeout(checkElement, 100);
        }
      };

      checkElement();
    });
  }

  setAudioDevice() {
    if (this.audioDeviceSelected !== '') {
      const constraints = {
        audio: {
          deviceId: { exact: this.audioDeviceSelected },
        },
      };

      this.audioStream?.getTracks().forEach((track) => track.stop());
      this.stopAudioLevelDetection();
      const sessionId = this.deviceSessionId;
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          if (this.deviceSessionId !== sessionId) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }
          // Stop the previous audioStream before overwriting — concurrent calls can race
          // and leave the loser's stream orphaned with live tracks.
          this.audioStream?.getTracks().forEach((track) => track.stop());
          this.audioStream = stream;
          this.startAudioLevelDetection(this.audioDeviceSelected);
        })
        .catch((error) => {
          if (this.deviceSessionId !== sessionId) return;
          // Log audio error
          this.logger.error('[CALL] device settings - error playing audio', error);

          this.displayErrorMessage(this.audioDevice, error);
          if (error.name === 'OverconstrainedError') {
            this.audioDeviceSelected = '';
            this.removeAudioDeviceLocalStorage();
            this.setAudioDevice();
            if (this.retryAudio === 0) {
              this.retryAudio++;
              this.listDevices(this.videoDevice);
            }
          }
        });
    }
  }

  errorAudioDevice(device: string, error: Error) {
    let { message } = error;
    if (
      message.includes('Requested device not found') ||
      message.includes('Permission denied')
    ) {
      if (device === this.audioDevice) {
        this.audioDeviceSelected = '';
        this.callStore.setIsAudioDeviceSelected(false);
        this.removeAudioDeviceLocalStorage();
        if (this.playerService) {
          if (
            this.callStore.getCurrentView() === AppConstants.VIEW_PRE_CALL
          ) {
            this.callStore.setCurrentView(AppConstants.VIEW_DEVICE_SETTINGS);
          }
        }
      }
    }
  }

  displayErrorMessage(device: string, error: Error) {
    let { message, name } = error;
    console.error(error);
    this.errorAudioDevice(device, error);
    console.log(`Error name: ${name}`);
    console.log(`Error message: ${message}`);

    const isAudioDevice = device === this.audioDevice;

    // show error message
    this.displayDeviceError(device, true);
    if (message.includes('Permission dismissed')) {
      if (isAudioDevice) {
        this.isPermissionDismissedAudio = true;
      } else {
        this.isPermissionDismissedVideo = true;
      }
      this.isDeviceBlocked = false;
    } else {
      this.isDeviceBlocked = true;
      if (isAudioDevice) {
        this.isPermissionDismissedAudio = false;
      } else {
        this.isPermissionDismissedVideo = false;
      }
       const strDevice = isAudioDevice ? `${this.translocoService.translate('MICROPHONE', {}, 'settings')}` : `${this.translocoService.translate('CAMERA', {}, 'settings')}`;
      message = `${strDevice} ${this.translocoService.translate('ACCESS_DENIED', {}, 'settings')}`;
      if (isAudioDevice) {
        this.audioDeviceSelected = '';
        this.callStore.setIsAudioDeviceSelected(false);
        this.removeAudioDeviceLocalStorage();
      }
    }

    // The OverconstrainedError interface of the Media Capture and Streams API indicates that the set of desired
    // capabilities for the current MediaStreamTrack cannot currently be met
    if (isAudioDevice && name.includes('OverconstrainedError')) {
      const currentView = this.callStore.getCurrentView();
      const isPrecall = currentView === AppConstants.VIEW_PRE_CALL;
      if (this.playerService && isPrecall) {
        this.callStore.setCurrentView(AppConstants.VIEW_DEVICE_SETTINGS);
        this.validateDeviceAccess();
      }
    }

    // Video
    if (device === this.videoDevice) {
      this.videoDevicePlaceholder = message;
    }

    // Audio
    if (isAudioDevice) {
      this.audioDevicePlaceholder = message;
    }
  }

  detectDeviceChanges() {
    navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);
  }

  private handleDeviceChange() {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      this.handleEnumerateDevices(devices);
    });
  }

  // Method to remove the devicechange event listener
  removeDeviceChangeListener() {
    navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler);
  }

  handleEnumerateDevices(devices: MediaDeviceInfo[]) {
    let isMicrophonePresent = false;
    let isCameraPresent = false;
    let isNewAudioDevice = false;
    let isNewVideoDevice = false;

    const deviceAudio = this.getAudioDeviceLocalStorage();
    const deviceVideo = this.getVideoDeviceLocalStorage();

    devices.forEach((device) => {
      if (device.deviceId === deviceAudio) {
        isMicrophonePresent = device.deviceId === deviceAudio;
      }
      if (device.deviceId === deviceVideo) {
        isCameraPresent = device.deviceId === deviceVideo;
      }
      if (device.kind === 'audioinput' && !isNewAudioDevice) {
        isNewAudioDevice = this.audioDeviceOptions.some((audioDevice) => {
          return device.deviceId !== audioDevice.deviceId;
        });
      }
      if (device.kind === 'videoinput' && !isNewVideoDevice) {
        isNewVideoDevice = this.videoDeviceOptions.some((videoDevice) => {
          return device.deviceId !== videoDevice.deviceId;
        });
      }
    });

    if (!isMicrophonePresent) {
      const item = this.getAudioLSName();
      localStorage.removeItem(item);
      localStorage.removeItem(this.lastAudioDevice);
      console.log('Microphone not present');
    }

    if (!isCameraPresent) {
      const item = this.getVideoLSName();
      localStorage.removeItem(item);
      localStorage.removeItem(this.lastVideoDevice);
      console.log('Cam not present');
    }
    const notFoundDevice = !isMicrophonePresent || !isCameraPresent;
    const newDevice = isNewAudioDevice || isNewVideoDevice;
    if (notFoundDevice || newDevice) {
      this.validateDeviceAccess();
    }
  }

  joinCall(saveDevices: boolean = true): void {
    if (!this.isJoinInProgress) {
      this.isJoinInProgress = true;
      this.stopDeviceSettings(saveDevices);

      const practiceType = this.callStore.getModuleAssignmentType();
      if (practiceType === PathwayConstants.PRACTICE_TYPE_PATHWAY) {
        this.callStore.setCurrentView('');
        // Emit event to notify pathway component that device settings are confirmed
        this.deviceSettingsConfirmed$.next();
        return;
      }

      // handle real time call
      if (this.controlService?.isRealTimeCall) {
        // Check cert acknowledgement pending before starting Tavus call
        if (this.callStore.getCertAcknowledgementPending()()) {
          if (this.playerService) {
            this.playerService.showLoadingScreen = false;
          }
          setTimeout(() => {
            this.callStore.setCurrentView(AppConstants.VIEW_PRE_CALL);
          }, 0);
          return;
        }
        this.controlService?.startCall();
        return;
      }

      // cert acknowledgement pending: navigate to pre-call screen where modal will auto-show
      // Do NOT call startPlayClips() here — callClips is empty because startCall() hasn't run yet
      if (this.callStore.getCertAcknowledgementPending()()) {
        if (this.playerService) {
          this.playerService.showLoadingScreen = false;
          this.playerService.isBeginCallDisabled = true;
        }
        setTimeout(() => {
          this.callStore.setCurrentView(AppConstants.VIEW_PRE_CALL);
        }, 0);
        return;
      }

      // start play call clips
      if (this.playerService) {
        this.playerService.startPlayClips();
      }
    }
  }

  stopDeviceSettings(saveDevices: boolean = true): void {
    this.deviceSessionId++; // cancel any in-flight getUserMedia callbacks
    if (saveDevices) {
      if (this.videoDeviceSelected) {
        this.saveVideoDevice(this.videoDeviceSelected);
      }
      if (this.audioDeviceSelected) {
        this.saveAudioDevice(this.audioDeviceSelected);
      }
    }

    const isVideoToggledOff = !this.controlService?.isVideoOn;
    if (isVideoToggledOff) {
      this.mediaStreamService.stopVideoStream();
    }

    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
    }

    this.audioStream = undefined;
    this.videoStream = undefined;

    this.stopAudioLevelDetection();
    this.removeDeviceChangeListener();

    this.logger.log('[CALL] device settings - stop');
  }

  startAudioLevelDetection(deviceAudio: string = ''): void {
    this.stopAudioLevelDetection();
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.isAudioLevelDetectionActive = true;
    const audioDeviceSaved = deviceAudio || this.getAudioDeviceLocalStorage();
    const constraints = audioDeviceSaved
      ? { audio: { deviceId: { exact: audioDeviceSaved } }, video: false }
      : { audio: true, video: false };

    this.logger.log('[CALL] device settings - start audio decibel level detection');

    const sessionId = this.audioLevelSessionId;
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        if (this.audioLevelSessionId !== sessionId) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        this.userAudioStream = stream;
        this.allAudioStreams.push(stream);

        this.microphone = this.audioContext.createMediaStreamSource(stream);
        this.microphone.connect(this.analyser);

        this.analyser.fftSize = 256;
        const bufferLength = this.analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        this.processAudio(dataArray);
      })
      .catch((error) => {
        if (this.audioLevelSessionId !== sessionId) return;
        console.error('Error accessing the microphone:', error);
        this.errorAudioDevice(this.audioDevice, error);
      });
  }

  processAudio(dataArray: any): void {
    this.analyser.getByteFrequencyData(dataArray);
    let total = 0;
    for (let i = 0; i < dataArray.length; i++) {
      total += dataArray[i];
    }
    const average = total / dataArray.length;
    this.decibels = 20 * Math.log10(average / 255);
    this.isUserSpeaking =
      this.decibels > -22 && !this.callStore.getIsAvatarSpeaking();
    this.animationFrame = requestAnimationFrame(() =>
      this.processAudio(dataArray)
    );
  }

  stopAudioLevelDetection(): void {
    this.audioLevelSessionId++; // cancel any in-flight getUserMedia from startAudioLevelDetection
    if (this.microphone) {
      this.microphone.disconnect();
      this.microphone = undefined;
    }
    this.allAudioStreams.forEach((stream) =>
      stream.getTracks().forEach((track) => track.stop())
    );
    this.allAudioStreams = [];
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = undefined;
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
    this.isAudioLevelDetectionActive = false;
  }

  displayDeviceSettingsModal(fromPauseModal: boolean = false): void {
    this.logger.log('[CALL] settings click');
    // Sending the Event of Clicking on Device Settings
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.CALL_CLICK_DEVICE_SETTINGS,
      {
        call_type: this.controlService?.callType,
      }
    );

    this.fromPauseModal = fromPauseModal;
    this.displayModal = true;
    const isRealTimeCall = this.controlService?.isRealTimeCall;
    // pause call
    if (!this.callStore.getIsPaused() && !this.callStore.getIsAudioCallMode()) {
      const isGuided = this.realtimeService?.isGuided();
      if (isRealTimeCall && isGuided) {
        if (this.controlService) {
          this.controlService.isMicOnBeforePause = this.callStore.getIsMicOn();
        }
        this.realtimeService?.pauseCall();
      } else {
        this.controlService?.pauseCall();
      }
    }
    this.validateDeviceAccess();
  }

  async confirmSettings(): Promise<void> {
    const isRealTimeCall = this.controlService?.isRealTimeCall;
    const fromPauseModal = this.fromPauseModal;
    this.fromPauseModal = false;

    // get current audio device and save it on variable
    const item = this.getAudioLSName();
    const currentAudioDevice = localStorage.getItem(item);

    // Log confirm settings click - distinguish between modal and screen
    const logMessage = this.displayModal
      ? '[CALL] device settings - modal - click confirm settings'
      : '[CALL] device settings - screen - click confirm settings';

    this.logger.log(logMessage, {
      selectedAudioDevice: this.audioDeviceSelected,
      selectedVideoDevice: this.videoDeviceSelected,
      fromPauseModal: this.fromPauseModal
    });

    this.displayModal = false;
    this.stopDeviceSettings();
    const isPractice =
      this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
    const isAudioUpdated = this.audioDeviceSelected !== currentAudioDevice;

    // Handle realtime call
    if (isRealTimeCall && this.realtimeService) {
      if (this.callStore.getIsAudioCallMode()) {
        this.stopAudioLevelDetection();
        return;
      }
      this.stopAudioLevelDetection();
      const isGuided = this.realtimeService?.isGuided();
      if (isGuided) {
        this.callStore.setIsResumeFromModal(!fromPauseModal);
        this.callStore.setResumeFromModalType('settings');
        this.realtimeService.resetAssemblyAudio(this.audioDeviceSelected, true);
        if (!fromPauseModal) {
          this.realtimeService?.unpauseCall();
        }
      }

      if (this.realtimeService.dailyCall && !isGuided) {
        await this.realtimeService.dailyCall
          .setInputDevicesAsync({
            audioDeviceId: this.audioDeviceSelected,
          })
          .then((result) => {
            console.log(
              'Microphone updated successfully:',
              result,
              this.audioDeviceSelected
            );
            this.startAudioLevelDetection(this.audioDeviceSelected);
          })
          .catch((error) => {
            console.error(
              'Error updating microphone:',
              error,
              this.audioDeviceSelected
            );
          });
      }
      return;
    }

    if (!fromPauseModal) {
      await this.controlService?.unpauseCall();
    }

    // if device is updated on modal screen and is display audio options on
    if (isAudioUpdated && this.displayAudioOptions && isPractice) {
      if (this.callStore.getIsMicOn() && this.playerService) {
        this.callStore.setSkipConnectTranscriber(true);
        this.playerService.restartTranscriber(true);
      }
    }
  }

  getAudioLSName(): string {
    const advance = this.callStore.getAdvanceType();
    const automatic = AppConstants.ADVANCE_TYPE_AUTOMATIC;
    const isAutomaticCall = advance === automatic;
    this.displayAudioOptions = true;
    const device = isAutomaticCall
      ? 'deviceAudioAutomatic'
      : 'deviceAudioManual';
    const uuid = this.controlService?.callUuid || '';
    return `${device}_${uuid}`;
  }

  getVideoLSName(): string {
    const advance = this.callStore.getAdvanceType();
    const automatic = AppConstants.ADVANCE_TYPE_AUTOMATIC;
    const isAutomaticCall = advance === automatic;
    const device = isAutomaticCall
      ? 'deviceVideoAutomatic'
      : 'deviceVideoManual';
    const uuid = this.controlService?.callUuid || '';
    return `${device}_${uuid}`;
  }

  saveAudioDevice(deviceId: string): void {
    this.audioDeviceSelected = deviceId;
    localStorage.setItem(this.getAudioLSName(), deviceId);
    localStorage.setItem(this.lastAudioDevice, deviceId);
  }

  saveVideoDevice(deviceId: string): void {
    this.videoDeviceSelected = deviceId;
    localStorage.setItem(this.getVideoLSName(), deviceId);
    localStorage.setItem(this.lastVideoDevice, deviceId);
  }

  getAudioDeviceLocalStorage(): string {
    const item = this.getAudioLSName();
    return localStorage.getItem(item) || '';
  }

  getVideoDeviceLocalStorage(): string {
    const item = this.getVideoLSName();
    return localStorage.getItem(item) || '';
  }

  removeAudioDeviceLocalStorage(): void {
    const item = this.getAudioLSName();
    console.log(`Removing item: ${item}`);
    localStorage.removeItem(item);
    localStorage.removeItem(this.lastAudioDevice);
  }

  removeVideoDeviceLocalStorage(): void {
    const item = this.getVideoLSName();
    console.log(`Removing item: ${item}`);
    localStorage.removeItem(item);
    localStorage.removeItem(this.lastVideoDevice);
  }

  givePermission(device: string): void {
    this.isFirstLoad = false;
    console.log('give permission');
    // validate if user has access to audio device
    if (device === this.audioDevice) {
      this.isPermissionDismissedAudio = false;
      this.getDeviceAccess(this.audioDevice, this.getAudioConstraints());
    }
    // validate if user has access to video device
    if (device === this.videoDevice && !this.controlService?.isVisualAidCall) {
      this.isPermissionDismissedVideo = false;
      this.getDeviceAccess(this.videoDevice, this.getVideoConstraints());
    }
  }

  getAudioConstraintsOtherCall(): MediaStreamConstraints {
    const audioDeviceSaved = localStorage.getItem(this.lastAudioDevice) || '';
    this.audioDeviceSaved = audioDeviceSaved;
    return this.audioDeviceSaved
      ? { audio: { deviceId: { exact: audioDeviceSaved } }, video: false }
      : { audio: true, video: false };
  }

  getVideoConstraintsOtherCall(): MediaStreamConstraints {
    const videoDeviceSaved = localStorage.getItem(this.lastVideoDevice) || '';
    this.videoDeviceSaved = videoDeviceSaved;
    return this.videoDeviceSaved
      ? { video: { deviceId: { exact: videoDeviceSaved } }, audio: false }
      : { video: true, audio: false };
  }

  validateAudioSetForCall(): boolean {
    const audio = this.getAudioDeviceLocalStorage();
    return audio.length > 0;
  }

  resetForNewCall(): void {
    this.isDiagnosticRunning = false;
    this.fromPauseModal = false;
    this.retryVideo = 0;
    this.retryAudio = 0;
    // NOT reset: device selection/saved/options — user preferences persist across calls
    // NOT reset: isJoinInProgress — already reset by resetRecognitionControls() at call end
    // NOT reset: isDeviceBlocked, isPermissionDismissed* — revalidated by validateDeviceAccess()
    // NOT reset: audioContext, analyser, microphone, animationFrame — managed by startAudioLevelDetection/stopAudioLevelDetection
  }
}
